import React, { useState, useEffect } from 'react';
import { Building2, Plus, Check, X } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface Company {
  id: string;
  name: string;
}

interface CompanySelectorProps {
  selectedCompany: string | null;
  onCompanyChange: (companyId: string | null) => void;
}

const CompanySelector: React.FC<CompanySelectorProps> = ({ selectedCompany, onCompanyChange }) => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [isAddingCompany, setIsAddingCompany] = useState(false);
  const [newCompanyName, setNewCompanyName] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    try {
      const { data, error } = await supabase
        .from('companies')
        .select('*')
        .order('created_at', { ascending: true });

      if (error) throw error;
      setCompanies(data || []);
      
      // If no company is selected and we have companies, select the first one
      if (!selectedCompany && data && data.length > 0) {
        onCompanyChange(data[0].id);
      }
    } catch (err) {
      console.error('Error loading companies:', err);
      setError('Failed to load companies');
    }
  };

  const handleAddCompany = async () => {
    if (!newCompanyName.trim()) {
      setError('Company name is required');
      return;
    }

    try {
      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;
      if (!user) throw new Error('No authenticated user');

      const { data, error } = await supabase
        .from('companies')
        .insert({ 
          name: newCompanyName.trim(),
          user_id: user.id
        })
        .select()
        .single();

      if (error) throw error;

      await loadCompanies();
      setIsAddingCompany(false);
      setNewCompanyName('');
      setError(null);
      
      // Select the newly created company
      if (data) {
        onCompanyChange(data.id);
      }
    } catch (err) {
      console.error('Error adding company:', err);
      setError(err instanceof Error ? err.message : 'Failed to add company');
    }
  };

  return (
    <div className="flex items-center gap-2">
      <select
        className="border rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#ff8200] focus:border-[#ff8200] outline-none"
        value={selectedCompany || ''}
        onChange={(e) => onCompanyChange(e.target.value || null)}
      >
        {companies.map(company => (
          <option key={company.id} value={company.id}>
            {company.name}
          </option>
        ))}
      </select>

      <button
        onClick={() => setIsAddingCompany(true)}
        className="bg-[#ff8200] hover:bg-[#e67600] text-white px-3 py-2 rounded-lg flex items-center"
      >
        <Plus className="h-5 w-5" />
      </button>

      {isAddingCompany && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Add New Company</h2>
              <button
                onClick={() => {
                  setIsAddingCompany(false);
                  setNewCompanyName('');
                  setError(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded-md">
                {error}
              </div>
            )}

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Company Name
              </label>
              <input
                type="text"
                className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#ff8200] focus:border-[#ff8200]"
                value={newCompanyName}
                onChange={(e) => setNewCompanyName(e.target.value)}
                placeholder="Enter company name"
              />
            </div>

            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setIsAddingCompany(false);
                  setNewCompanyName('');
                  setError(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAddCompany}
                className="bg-[#ff8200] hover:bg-[#e67600] text-white px-4 py-2 rounded-lg flex items-center"
              >
                <Check className="h-5 w-5 mr-1" />
                Add Company
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanySelector;